import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, ListItemButton, Tooltip, Typography } from "@mui/material";
import { useTheme, type CSSObject, type Theme } from "@mui/material/styles";
import { Avatar, IconButton, type AvatarColor } from "@outpave/ds";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  Alert02Icon,
  CheckmarkCircle02Icon,
  Comment01Icon,
  CreditCardIcon,
  Delete02Icon,
  InformationCircleIcon,
  Invoice01Icon,
  MoneyReceive02Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Inline, Stack } from "components/layout/reusableLayouts";
import NotificationStatus from "../../enums/NotificationStatus";

interface RenderNotificationRowProps {
  /** Opens the notification's record; omit when it has none, and the row is not a button. */
  onOpen?: () => void;
  notificationType: string | null;
  notificationBody: string | null;
  notificationActor?: string | null;
  notificationTime: string;
  notificationStatus: string;
  markNotificationAsRead: () => void;
  markNotificationAsDeleted: () => void;
}

const DANGER_TYPES = new Set([
  "ACCOUNT_DISCONNECTED",
  "CARD_REACH_DAILY_LIMIT",
  "CARD_REACH_SPENDING_LIMIT",
  "CARD_REQUEST_INCREASE_SPENDING_DENIED",
  "CARD_REQUEST_NEW_CARD_DENIED",
  "CARD_REQUEST_NEW_MERCHANT_DENIED",
  "CARD_STATUS_CANCELED",
  "CARD_STATUS_LOST_CARD",
  "CARD_STATUS_STOLEN_CARD",
  "INSUFFICIENT_ACCOUNT",
  "PAYMENT_FAIL",
  "REIMBURSEMENT_PAYMENT_FAIL",
  "SUSPICIOUS_ACTIVITIES_ALERT",
  "TRANSACTION_RECEIPT_ERROR",
  "WORKFLOW_REQUEST_REIMBURSEMENT_DENIED",
]);

const WARNING_TYPES = new Set([
  "CARD_APPROACH_DAILY_LIMIT",
  "CARD_APPROACH_LIMIT",
  "CARD_APPROACH_SPENDING_LIMIT",
  "CARD_STATUS_FROZEN",
  "CARD_STATUS_PRE_CANCEL",
  "TRANSACTION_ADMIN_REMINDER",
  "TRANSACTION_MISSING_ALLOCATION",
  "TRANSACTION_MISSING_ALLOCATION_ALERT",
  "TRANSACTION_MISSING_ATTENDEES",
  "TRANSACTION_MISSING_COST_ALLOCATION",
  "TRANSACTION_MISSING_RECEIPT",
  "TRANSACTION_MISSING_RECEIPT_ALERT",
  "TRANSACTION_NOT_MATCHED",
  "TRANSACTION_TASK_PENDING_COMPLIANCE",
]);

const BRAND_TYPES = new Set([
  "CARD_REQUEST_INCREASE_SPENDING",
  "CARD_REQUEST_INCREASE_SPENDING_APPROVAL",
  "CARD_REQUEST_NEW_CARD",
  "CARD_REQUEST_NEW_CARD_APPROVAL",
  "CARD_STATUS_NEW_CARD",
]);

const toneFor = (type: string): AvatarColor => {
  if (DANGER_TYPES.has(type)) return "danger";
  if (WARNING_TYPES.has(type)) return "warning";
  if (BRAND_TYPES.has(type)) return "brand-soft";
  return "default";
};

const glyphFor = (type: string, tone: AvatarColor): IconSvgElement => {
  if (type.endsWith("_NEW_COMMENT") || type === "TRANSACTION_ADMIN_NOTE") {
    return Comment01Icon;
  }
  if (type.startsWith("CARD_")) return CreditCardIcon;
  if (type.includes("REIMBURSEMENT")) return MoneyReceive02Icon;
  if (type.includes("TRANSACTION") || type.includes("RECEIPT")) {
    return Invoice01Icon;
  }
  return tone === "danger" ? Alert02Icon : InformationCircleIcon;
};

/** Tile tint and glyph for a notification, keyed on its GraphQL `NotificationType`. */
export const notificationAppearance = (type: string) => {
  const tone = toneFor(type);
  return { tone, glyph: glyphFor(type, tone) };
};

/** The fallback both the client title and the server body use when they have nothing to say. */
const GENERIC_MESSAGE = "You have received a notification";

// Types whose body already names who did what, so the title only repeats it. Bodies are
// English-only server copy, so outside English the translated title stays.
const BODY_ONLY_TYPES = new Set([
  "CARD_REQUEST_NEW_CARD_APPROVAL",
  "CARD_REQUEST_INCREASE_SPENDING_APPROVAL",
  "WORKFLOW_REQUEST_REIMBURSEMENT_APPROVAL",
]);

/** Which parts of a notification to show, dropping the generic fallback wherever a real part exists. */
export const notificationText = (
  type: string,
  title: string,
  body: string,
  language = "en",
): { title?: string; body?: string } => {
  const titleRepeatsBody =
    BODY_ONLY_TYPES.has(type) && language.toLowerCase().startsWith("en");
  const hasTitle = title !== GENERIC_MESSAGE && !titleRepeatsBody;
  const hasBody = Boolean(body) && body !== GENERIC_MESSAGE;
  if (!hasTitle && !hasBody) return { title };
  return {
    title: hasTitle ? title : undefined,
    body: hasBody ? body : undefined,
  };
};

/** The body with the actor's name bolded where it leads the sentence; any later mention stays plain. */
export const emphasizeActor = (
  body: string,
  actor?: string | null,
): React.ReactNode => {
  if (!actor || !body.startsWith(actor)) return body;
  const rest = body.slice(actor.length);
  // "Ann" must not bold the start of "Anna".
  if (/^[\p{L}\p{N}]/u.test(rest)) return body;
  return [<strong key="actor">{actor}</strong>, rest];
};

const clampSx = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 3,
  overflow: "hidden",
  overflowWrap: "anywhere",
  textWrap: "pretty",
} as const;

const contentSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: "var(--space-4)",
  flex: 1,
  minWidth: 0,
  paddingY: "var(--space-4)",
  paddingLeft: "var(--space-5)",
  paddingRight: "var(--space-3)",
} as const;

/** One state rule of the DS ListItemButton override, so the row paints what the package decides. */
const listItemButtonRule = (theme: Theme, selector: string): CSSObject => {
  const root = theme.components?.MuiListItemButton?.styleOverrides?.root;
  if (!root || typeof root !== "object" || Array.isArray(root)) return {};
  const rule = (root as Record<string, unknown>)[selector];
  return rule && typeof rule === "object" ? (rule as CSSObject) : {};
};

// An inset ::after stretches the button over the row, so the row opens and highlights as one.
const stretchedButtonSx = {
  ...contentSx,
  position: "static",
  "&:hover": { backgroundColor: "transparent" },
  "&.Mui-focusVisible": { backgroundColor: "transparent", boxShadow: "none" },
  "&::after": { content: '""', position: "absolute", inset: 0 },
} as const;

const actionsSx = {
  position: "relative",
  zIndex: 1,
  paddingTop: "var(--space-4)",
  paddingRight: "var(--space-5)",
} as const;

export const RenderNotificationRow = ({
  onOpen,
  notificationType,
  notificationBody,
  notificationActor,
  notificationTime,
  notificationStatus,
  markNotificationAsRead,
  markNotificationAsDeleted,
}: RenderNotificationRowProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  // GraphQL types the field as nullable, and a null type must not take the whole panel down.
  const type = notificationType ?? "";
  const { title, body } = notificationText(
    type,
    type ? t(`notificationMessages.${type}`, GENERIC_MESSAGE) : GENERIC_MESSAGE,
    notificationBody ?? "",
    i18n?.language,
  );
  const { tone, glyph } = notificationAppearance(type);
  // Tracked in state rather than with :has(), which this repo's jsdom cannot be trusted to parse.
  const [focusVisible, setFocusVisible] = useState(false);

  const content = (
    <>
      <Avatar size="lg" color={tone} icon={<HugeiconsIcon icon={glyph} />} />
      <Stack gap="var(--space-1)" sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={clampSx}>
          {title && <strong>{title}</strong>}
          {title && body && " "}
          {body && emphasizeActor(body, notificationActor)}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {notificationTime}
        </Typography>
      </Stack>
    </>
  );

  return (
    <Inline
      gap="0"
      align="start"
      wrap={false}
      sx={{
        width: "100%",
        ...(onOpen
          ? {
              position: "relative",
              "&:hover": listItemButtonRule(theme, "&:hover"),
              ...(focusVisible
                ? listItemButtonRule(theme, "&.Mui-focusVisible")
                : {}),
            }
          : {}),
      }}
    >
      {/* The actions sit beside the button, not in it: a control inside a button is unreachable. */}
      {onOpen ? (
        <ListItemButton
          onClick={onOpen}
          onFocusVisible={() => setFocusVisible(true)}
          onBlur={() => setFocusVisible(false)}
          sx={stretchedButtonSx}
        >
          {content}
        </ListItemButton>
      ) : (
        <Box sx={contentSx}>{content}</Box>
      )}
      <Inline gap="var(--space-2)" wrap={false} sx={actionsSx}>
        {notificationStatus === NotificationStatus.UNREAD && (
          <Tooltip title={t("Mark as Read")} placement="bottom">
            <IconButton
              aria-label={t("Mark as Read")}
              variant="ghost"
              size="small"
              onClick={markNotificationAsRead}
            >
              <HugeiconsIcon icon={CheckmarkCircle02Icon} size={18} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t("Delete Notification")} placement="bottom">
          <IconButton
            aria-label={t("Delete Notification")}
            variant="ghost"
            color="danger"
            size="small"
            onClick={markNotificationAsDeleted}
          >
            <HugeiconsIcon icon={Delete02Icon} size={18} />
          </IconButton>
        </Tooltip>
      </Inline>
    </Inline>
  );
};
